import EventEmitter from "events";
import React, { FC, MutableRefObject, useState, useEffect, useRef, useCallback, useMemo } from "react";
import Link from "next/link";
import { styled } from "@mui/material/styles";
import Image, { StaticImageData } from "next/legacy/image";
import { useSnackbar } from "notistack";
import useFullpage from "@/hooks/useFullpage"
import { debounce } from "@mui/material/utils";

import Mask from "@/components/common/mask"
import HoverTip from "@/components/common/hover-tip"
import BucketHover from "@/components/points/bucket-hover"
import Loading from "@/components/loading/points"
import BrewingModal from "@/components/points/brewing-modal"
import RecordModal from "@/components/points/record-modal"
import { ConfirmModal } from "@/components/confirm-modal"

import MallArrowIcon from "@/public/img/points/mall-arrow.svg"
import PointsIcon from '@/public/img/points/points-icon.svg'
import PointsRecoup from '@/public/img/points/points-recoup.svg'

import BartenderTalkAnimate from "@/public/img/points/bartender-talk.webp"
import BartenderGetPointsAnimate from "@/public/img/points/bartender-get-points.webp"

import PointsMainBg from "@/public/img/points/points-main-bg.png"
import PointsBg2 from "@/public/img/points/points-bg2.png"
import PointsShelf from "@/public/img/points/points-shelf.png"
import PointsBartender from "@/public/img/points/points-bartender.png"
import PointsBucket from "@/public/img/points/points-bucket.png"
import PointsBucketLight from "@/public/img/points/points-bucket-light.png"

import { preloadEx } from "@/utils/preloader"
import { typedWord } from "@/utils/animation"
import { isMobile as checkIsMobile, formatThousand } from "@/utils/util"

import { RecoupInfo, PointsBillInfo, PointsBrewingInfo } from "@/types/points"

import "cropperjs/dist/cropper.css";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

import { GetProfile, getOwnPoints, claimRecoupPoints, getPointsBill, archiveUnbrewPoints, getUnbrewReceipt } from "@/utils/api"

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "points"])),
    },
  };
}

const staticImageDataToAsset = (imageData: StaticImageData[] = []) => {
  return imageData.map(({ src }) => {
    return {
      type: 'image',
      src
    }
  })
}

type RecoupInfos = Array<RecoupInfo>

type PointsBillInfos = {
  income: string | number
  outcome: string | number
  receipts: Array<PointsBillInfo>
}

type PointsBrewingInfos = Array<PointsBrewingInfo>

interface PointsProps {
  className?: string
}

const _Points: FC<PointsProps> = ({ className }) => {
  // 信息提示框组件函数
  const { enqueueSnackbar } = useSnackbar();
  // 加载进度
  const [progress, setProgress] = useState(0)
  // loading 态显示状态
  const [loading, setLoading] = useState(true)
  // 是否装载 Main
  const [isReady, setIsReady] = useState(false)
  // 可用积分
  const [points, setPoints] = useState(0)
  // 补偿积分
  const [recoup, setRecoup] = useState<RecoupInfos>([])
  // 酿造列表
  const [brewingItems, setBrewingItems] = useState<PointsBrewingInfos>([])

  // 自定义样式
  const [customStyle, setCustomStyle] = useState({})
  // 计算屏幕高度
  const { innerHeight } = useFullpage()
  const isMobile = useMemo(() => checkIsMobile(), [])
  // 内容缩放尺寸，用于mobile状态时放大内容
  const [scale, setScale] = useState(1)

  // mobile 逻辑
  useEffect(() => {
    if (!isMobile) return
    setCustomStyle({ height: `${innerHeight}px`, overflow: 'auto' })
    setScale(2)
  }, [innerHeight, isMobile])

  const events = useMemo(() => {
    return new EventEmitter()
  }, [])

  const getFetcher = useCallback((request, args = []) => {
    return async () => {
      const { data, err } = await request(...args);
      if (err) {
        if (err.response?.data?.code === 2) {
          location.href = "/mall";
        }
        enqueueSnackbar("Unable to connect to the network!", {
          variant: "error",
          preventDuplicate: true,
        });
        throw err;
      }
      return { data, err };
    };
  }, [enqueueSnackbar])

  // 获取积分状态
  const getOwnPointsHandler = useCallback(async () => {
    const d = await getFetcher(getOwnPoints)();
    if (d.err) return

    setPoints(Number(d.data.total))
    setRecoup(d.data.compensations)
  }, [getFetcher])

  // 酿造明细
  const getUnbrewReceiptHandler = useCallback(async (receiptId: string | number) => {
    const d = await getFetcher(getUnbrewReceipt, [receiptId])()
    if (d.err) return
    setBrewingItems(d.data.tokenIncome)
  }, [getFetcher])

  // listen progress change
  useEffect(() => {
    let timer = null
    if (progress === 100) {
      setIsReady(true)
      timer = setTimeout(() => {
        setLoading(false)
        clearTimeout(timer)
        timer = null
      }, 300);
    }
    return () => {
      timer = null
    }
  }, [progress])

  useEffect(() => {
    preloadEx({
      assets: staticImageDataToAsset([PointsMainBg, PointsBg2, PointsShelf, PointsBartender, PointsBucket, PointsBucketLight]),
      onProgress: (progress) => {
        setProgress(progress * 90)
        if (progress === 1) {
          Promise.all([
            getOwnPointsHandler(),
            getUnbrewReceiptHandler(0),
          ]).then(() => {
            setProgress(95)
            setTimeout(() => {
              setProgress(100)
            }, 2000);
          })
        }
      }
    })
  }, [getUnbrewReceiptHandler, getOwnPointsHandler])

  // 积分开桶
  const archiveUnbrewHandler = useCallback(async () => {
    const d = await archiveUnbrewPoints()
    if (d.err) {
      enqueueSnackbar("Unable to connect to the network!", {
        variant: "error",
        preventDuplicate: true,
      });
      return
    }
  }, [enqueueSnackbar])

  useEffect(() => {
    // 监听 unbrew 事件，执行开桶逻辑
    events.on('api:archive', () => {
      archiveUnbrewHandler()
    })

    // 监听积分刷新事件，刷新当前积分状态
    events.on('api:refresh-points', () => {
      getOwnPointsHandler()
    })
  }, [events, archiveUnbrewHandler, getOwnPointsHandler])

  return (
    <div className={`relative w-full h-screen overflow-hidden bg-[#010A32] points-main ${className}`} style={customStyle}>
      {
        loading && (
          <Mask fullpage progress={progress}>
            <Loading current={progress}/>
          </Mask>
        )
      }
      {
        isReady && <Main events={events} isMobile={isMobile} scale={scale} points={points} brewingItems={brewingItems} recoup={recoup}/>
      }
    </div>
  );
}

const Points = styled(_Points)`
  &.points-main {
    min-height: 796px;
    @media (max-width: 440px) {}
    @media (min-width: 1920px) {
      min-height: 976px;
    }
    @media (min-width: 2560px) {
      min-height: 1300px;
    }
  }
`

export default Points

interface MainProps {
  className?: string;
  points?: number
  recoup?: RecoupInfos
  brewingItems?: PointsBrewingInfos
  events?: EventEmitter
  scale?: number
  isMobile?: boolean
}

const _Main: FC<MainProps> = ({ className, events, scale, isMobile, points, brewingItems, recoup }) => {
  const { t } = useTranslation('points')
  const mainRef = useRef<HTMLDivElement>()
  const roomRef = useRef<HTMLDivElement>()
  const [showRecoup, setShowRecoup] = useState(true)

  // mobile 按钮样式（放大）
  const btnMobileStyle = useMemo(() => {
    return { transform: `scale(${scale})` }
  }, [scale])

  // mobile 容器样式（增加宽度）
  const wrapMobileStyle = useMemo(() => {
    if (!isMobile) return {}
    return { minWidth: '2200px' }
  }, [isMobile])

  // mobile mounted: 页面滚动到酒保位置
  useEffect(() => {
    if (!isMobile) return
    mainRef.current.parentElement.scrollLeft = 800
  }, [isMobile])

  // 待补偿积分汇总
  const recoupPoints = useMemo(() => {
    return recoup.reduce((total, item) => {
      total += Number(item.income)
      return total
    }, 0)
  }, [recoup])

  // 开场话术内容
  const loadedWords = useMemo(() => {
    return [
      t('rule1'),
      t('rule2'),
    ]
  }, [t])

  // loaded event
  const loadedSpeak = useCallback(() => {
    setTimeout(() => {
      events.emit('speak', loadedWords[Math.floor(Math.random() * loadedWords.length)], 'loaded')
    }, 1000);
  }, [events, loadedWords])

  // mounted
  useEffect(() => {
    loadedSpeak()
  }, [loadedSpeak])

  // claim recoup
  const confirmRecoupHandle = async () => {
    const res = await claimRecoupPoints()
    if (res.err) return
    setShowRecoup(false)
    events.emit('speak', t('get-points-speak'), 'get-points')
  }

  return (
    <div ref={mainRef} className={`relative w-full h-full overflow-hidden wrap ${className}`} style={wrapMobileStyle}>
      { (showRecoup && recoupPoints) ? <Recoup scale={scale} recoupPoints={recoupPoints} confirmRecoup={confirmRecoupHandle}/> : null }
      <div className="pointer-events-none fixed top-[100px] bottom-[100px] left-[60px] right-[60px] z-[8] points-fixed">
        <div className="absolute top-[24px] left-0 origin-top-left" style={btnMobileStyle}>
          <Record scale={scale} points={points} />
        </div>
        <div className="absolute right-0 bottom-[24px] origin-bottom-right" style={btnMobileStyle}>
          <Unbrew events={events} />
        </div>
      </div>
      <div ref={roomRef} className="absolute pointer-events-none points-room z-[1]">
        <div className="absolute points-room-tarven overflow-hidden z-0">
          <div className="absolute w-full h-full points-room-tarven-bg bg-cover z-[1]" style={{ backgroundImage: `url(${PointsBg2.src})` }}></div>
          <div className="absolute w-full h-full points-room-tarven-ctnr z-[2]">
            <TarvenShelf scale={scale} events={events} brewingItems={brewingItems} containerRef={roomRef}/>
          </div>
        </div>
        <div className="absolute w-full h-full points-room-full bg-cover z-[6]" style={{ backgroundImage: `url(${PointsMainBg.src})` }}></div>
        <div className="absolute pointer-events-auto select-none points-room-bar z-[8]">
          <TarvenBar events={events}/>
        </div>
      </div>
    </div>
  )
}

const Main = styled(_Main)`
  &.wrap {
    .points-room {
      left: 50%;
      bottom: 50%;
      transform: translateX(-50%) translateY(43%);
      width: 1552px;
      height: 873px;
      @media (min-width: 1920px) {
        bottom: 50%;
        transform: translateX(-50%) translateY(43%);
        width: 2034px;
        height: 1144px;
      }
      @media (min-width: 2560px), (max-width: 440px)  {
        bottom: 50%;
        transform: translateX(-50%) translateY(43%);
        width: 2713px;
        height: 1526px;
      }
      @media (max-width: 440px) {
        transform: translateX(-50%) translateY(50%);
      }
      &-tarven {
        bottom: 190px;
        left: 297px;
        width: 592px;
        height: 371px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          bottom: 250px;
          left: 390px;
          width: 776px;
          height: 486px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          bottom: 350px;
          left: 520px;
          width: 1034px;
          height: 648px;
        }
        &-bg {

        }
        &-ctnr {
          bottom: 0;
          left: 37px;
          width: 371px;
          height: 229px;
          @media (max-width: 440px) {}
          @media (min-width: 1920px) {
            left: 48px;
            width: 486px;
            height: 300px;
          }
          @media (min-width: 2560px), (max-width: 440px)  {
            left: 65px;
            width: 648px;
            height: 400px;
          }
        }
      }
      &-bar {
        right: 284px;
        bottom: 160px;
        width: 310px;
        height: 337px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          right: 370px;
          bottom: 196px;
          width: 411px;
          height: 450px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          right: 494px;
          bottom: 280px;
          width: 548px;
          height: 600px;
        }
      }
      &-full {
        top: 0;
        left: 0;
        background-position: center 15px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          background-position: center 26px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          background-position: center 20px;
        }
      }
    }
  }
`

interface RecordProps extends MainProps {}

/**
 * 组件：积分相关
 */
const Record: FC<RecordProps> = ({ points, scale }) => {
  const { t } = useTranslation("points")
  const [pointsBill, setPointsBill] = useState<PointsBillInfos>()
  const [openRecordModal, setOpenRecordModal] = useState(false)
  const getPointsBillHandler = useCallback(async () => {
    const d = await getPointsBill()
    if (d.err) return
    setPointsBill(d.data)
  }, [])

  const closeRecordModalHandle = () => {
    setOpenRecordModal(false)
  }
  const openRecordModalHandle = async () => {
    await getPointsBillHandler()
    setOpenRecordModal(true)
  }
  return (
    <>
      { openRecordModal && <RecordModal scale={scale} points={points} income={pointsBill.income} outcome={pointsBill.outcome} receipts={pointsBill.receipts} onClose={closeRecordModalHandle} /> }
      <div className="pointer-events-auto flex items-center">
        <div className="flex-1 flex items-center pl-[24px] pr-[12px] py-[9px] rounded-l-[100px] bg-[#395AFF] border-solid border-[3px] border-r-0 border-[#000]">
          <PointsIcon className="w-[46px] h-[52px] cursor-pointer" onClick={openRecordModalHandle}/>
          <p className="flex-1 m-0 ml-[5px] min-w-[117px] h-[44px] font-black text-[40px] leading-[44px] text-white">{ formatThousand(points) }</p>
        </div>
        <div className="relative w-[100px] flex flex-col">
          <p className="m-0 absolute left-0 top-[50%] translate-y-[-50%] w-full h-[3px] bg-black z-[8]"></p>
          <button
            type="button"
            className="m-0 w-full border-[3px] border-b-[1px] border-solid border-black rounded-tr-[20px] overflow-hidden px-[19px] py-[9px] text-[14px] leading-[16px] text-white font-bold bg-[#395AFF] hover:bg-white hover:text-black cursor-pointer"
            onClick={openRecordModalHandle}>
            { t("record") }
          </button>
          <a target="_blank" href="/pdf/CUP Points Introduction.pdf" rel="noreferrer">
            <button
              type="button"
              className="m-0 w-full border-[3px] border-t-[1px] border-solid border-black rounded-br-[20px] px-[19px] py-[9px] text-[14px] leading-[16px] text-white font-bold bg-[#395AFF] hover:bg-white hover:text-black cursor-pointer">
              { t("intro") }
            </button>
          </a>
        </div>
      </div>
    </>
  )
}

interface UnbrewProps extends MainProps {
}

/**
 * 组件：开桶相关按钮
 */
const _Unbrew: FC<UnbrewProps> = ({ className, events }) => {
  const { t } = useTranslation("points");

  const unbrewClickHandler = () => {
    events?.emit('action:unbrew')
  }
  return (
    <>
      <div className={`pointer-events-auto flex gap-[8px] items-center ${className}`}>
        <div className="relative unbrew-item unbrew-open">
          <button type="button" className="m-0 border-none relative py-[16px] text-center font-black text-white text-[18px] leading-[18px] rounded-[28px] unbrew-item-btn unbrew-open-btn" onClick={debounce(unbrewClickHandler, 300)}>
            { t('unbrew-all').toUpperCase() }
          </button>
        </div>
        <div className="relative unbrew-item unbrew-mall">
          <Link href={{ pathname: '/mall' }} target="_blank">

            <button type="button" className="m-0 border-none flex items-center justify-center gap-[8px] relative py-[16px] text-center font-black text-black text-[18px] leading-[18px] rounded-[28px] unbrew-item-btn unbrew-mall-btn">
            { t('mall').toUpperCase() }
            <MallArrowIcon className="w-[19px] h-[14px]" />
          </button>

          </Link>
        </div>
      </div>
    </>
  );
}

const Unbrew = styled(_Unbrew)`
  .unbrew {
    &-item {
      &::before {
        content: '';
        position: absolute;
        top: 8px;
        left: 0;
        border: 3px solid #000000;
        border-radius: 28px;
        z-index: -1;
        pointer-events: none;
      }
      &-btn {
        transform: translateY(0px);
        transition: transform .15s;
        cursor: pointer;
        z-index: 2;
      }
      &:hover {
        .unbrew-item-btn {
          transform: translateY(8px);
        }
      }
    }
    &-open {
      width: 328px;
      height: 56px;
      &::before {
        width: 322px;
        height: 50px;
        background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), linear-gradient(90deg, #0096FF 0%, #BC2AEF 50.83%, #FF2800 100%);
      }
      &-btn {
        width: 328px;
        background-image: linear-gradient(to right, #0096FF, #BC2AEF, #FF2800);
        border: 3px solid #000000;
        border-radius: 28px;
      }
    }
    &-mall {
      width: 160px;
      height: 56px;
      &::before {
        width: 154px;
        height: 50px;
        background-color: #484C53;
      }
      &-btn {
        width: 160px;
        background-color: #fff;
        border: 3px solid #000000;
        border-radius: 28px;
      }
    }
  }
`

interface RecoupProps extends MainProps {
  recoupPoints: number
  confirmRecoup: () => void
}

/**
 * 组件：领取补偿
 */
const _Recoup: FC<RecoupProps> = ({ className, confirmRecoup, recoupPoints }) => {
  const { t } = useTranslation('points')
  return (
    <div className={`pointer-events-auto select-none fixed bg-black/60 top-[100px] bottom-[100px] left-0 right-0 z-[9] points-recoup-fixed ${className}`}>
      <div className="absolute bottom-[24px] left-[60px] w-[500px] h-[196px] points-fixed-recoup">
        <div className="relative select-auto w-full h-full pl-[40px] pr-[24px] text-black bg-white rounded-[40px] flex items-start points-recoup-ctnr">
          <div className="flex-1 flex flex-col min-w-0 h-full justify-around py-[24px]">
            <div className="flex-1">
              <h2 className="m-0 text-[32px] leading-[32px] font-DIN_Alternate">{ t('recoup-tit') }</h2>
              <p className="m-0 mt-[8px] text-[12px] leading-[20px]">
                <span className="font-bold">{ t('recoup-desc-prefix') }</span>
                { t('recoup-desc') }
              </p>
            </div>
            <button className="px-[24px] py-[16px] border-none rounded-[24px] cursor-pointer bg-black text-[16px] text-white font-medium leading-[16px]" type="button" onClick={confirmRecoup}>
              { t('recoup-btn-prefix') }
              <span className="text-[#FF2800] font-black"> { formatThousand(recoupPoints) } </span>
              { t('recoup-btn-suffix') }
            </button>
          </div>
          <div className="relative w-[206px] h-full">
            <PointsRecoup className="absolute bottom-0 left-0 w-full"/>
          </div>
        </div>
      </div>
    </div>
  )
}

const Recoup = styled(_Recoup)`
`

interface TarvenShelfProps extends MainProps {
  containerRef?: MutableRefObject<HTMLDivElement>
}

/**
 * 组件：酒架内容
 */
const _TarvenShelf: FC<TarvenShelfProps> = ({ className, scale, containerRef, brewingItems, events }) => {
  const { t } = useTranslation('points')
  const shelfRef = useRef()
  const [showBrewingModal,  setShowBrewingModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    // 执行开桶
    events.on('action:unbrew', () => {
      if (!brewingItems.length || (shelfRef.current as HTMLElement)?.classList?.contains('unbrew')) {
        events?.emit('speak', t('empty-unbrew-speak'), 'empty-unbrew')
        return
      }
      setShowConfirmModal(true)
    })
  }, [events, brewingItems, t])

  const openBrewingModal = (info) => {
    setShowBrewingModal(true)
  }
  const closeBrewingModal = () => {
    setShowBrewingModal(false)
  }

  const confirmModalHandler = (type: string) => {
    setShowConfirmModal(false)
    if (type !== 'button') return
    events.emit('api:archive')
    ;(shelfRef.current as HTMLElement)?.classList?.add('unbrew')
    // 酒架动画结束，执行酒保积分动画
    setTimeout(() => {
      events?.emit('action:get-points')
    }, 1500);
  }
  return (
    <div ref={shelfRef} className={`relative w-full h-full tarven-shelf-wrap ${className}`}>
      {showBrewingModal && <BrewingModal scale={scale} items={brewingItems} onClose={closeBrewingModal}/>}
      {
        showConfirmModal && (
          <ConfirmModal
            title={t('unbrew-modal-tit')}
            desc={t('unbrew-modal-desc')}
            noOkBtn
            onCancel={confirmModalHandler}
            cancelText={t('unbrew-modal-confirm')} />
        )
      }
      <div className="absolute w-full h-full tarven-shelf bg-cover z-[1]" style={{ backgroundImage: `url(${PointsShelf.src})` }}></div>
      <ul className="absolute m-0 p-0 w-full h-full pointer-events-auto list-none tarven-buckets z-[2]">
        {
          brewingItems.slice(0, 7).map((info, idx) => {
            return (
              <li key={idx} className="absolute tarven-bucket z-[1]" data-idx={idx}>
                <BucketHover containerRef={containerRef} info={info} driection={(idx !== 6 && idx % 2 == 0) ? 'top' : 'bottom'} offset={-21} bucketSource={PointsBucket} bucketLightSource={PointsBucketLight} openModal={openBrewingModal} />
              </li>
            )
          })
        }
      </ul>
    </div>
  )
}

const TarvenShelf = styled(_TarvenShelf)`
  &.tarven-shelf-wrap {
    @keyframes unbrew-shelf {
      0% {
        transform: translateX(0);
      }
      50% {
        transform: translateX(-100%);
      }
      60% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(0);
      }
    }
    &.unbrew {
      pointer-events: none;
      animation: unbrew-shelf 1.2s ease-in-out 1;
      .tarven-buckets {
        pointer-events: none;
        visibility: hidden;
        transition: visibility 0s .6s;
      }
    }
  }
  .tarven {
    &-shelf {

    }
    &-buckets {
      bottom: 4px;
      right: 28px;
      width: 398px;
      height: 204px;
      @media (max-width: 440px) {
        bottom: 8px;
        right: 50px;
        width: 694px;
        height: 357px;
      }
      @media (min-width: 1920px) {
        bottom: 6px;
        right: 38px;
        width: 521px;
        height: 268px;
      }
      @media (min-width: 2560px), (max-width: 440px)  {
        bottom: 8px;
        right: 50px;
        width: 694px;
        height: 357px;
      }
    }
    &-bucket {
      right: calc(var(--x));
      bottom: calc(var(--y));
      width: 130px;
      height: 114px;
      @media (max-width: 440px) {}
      @media (min-width: 1920px) {
        width: 171px;
        height: 150px;
      }
      @media (min-width: 2560px), (max-width: 440px)  {
        width: 228px;
        height: 200px;
      }
      &:nth-of-type(2n + 1) {
        --y: 88px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --y: 115px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --y: 154px;
        }
      }
      &:nth-of-type(2n) {
        --y: 0px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --y: 0px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --y: 0px;
        }
      }
      &[data-idx="0"] {
        --x: 32px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 42px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 56px;
        }
      }
      &[data-idx="1"] {
        --x: 0px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 0px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 0px;
        }
      }
      &[data-idx="2"] {
        --x: 120px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 157px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 210px;
        }
      }
      &[data-idx="3"] {
        --x: 86px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 114px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 152px;
        }
      }
      &[data-idx="4"] {
        --x: 205px;
        --y: 88px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 270px;
          --y: 115px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 360px;
          --y: 154px;
        }
      }
      &[data-idx="5"] {
        --x: 173px;
        --y: 0px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 228px;
          --y: 0px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 304px;
          --y: 0px;
        }
      }
      &[data-idx="6"] {
        --x: 260px;
        --y: 0px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          --x: 342px;
          --y: 0px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          --x: 456px;
          --y: 0px;
        }
      }
    }
  }
`

interface TarvenBarProps extends MainProps {
}

/**
 * 组件：酒吧内容
 */
const _TarvenBar: FC<TarvenBarProps> = ({ className, events }) => {
  const { t } = useTranslation('points')
  const bubbleRef = useRef()
  const [word, setWord] = useState('')
  const [isSpeak, setIsSpeak] = useState(false)
  const [isGetPoints, setIsGetPoints] = useState(false)
  const bartenderWords = [
    t('bartender-speak1'),
    t('bartender-speak2'),
    t('bartender-speak3'),
    t('bartender-speak4')
  ]

  useEffect(() => {
    // 积分获取动画时长：5000ms
    // 监听获取积分事件：播放动画 -3000ms-> speak -> (2000ms)暂停动画 -> end
    events.on('action:get-points', () => {
      events?.emit('speak', '', 'clear')
      setIsGetPoints(true)
      setTimeout(() => {
        // 积分信息刷新
        events?.emit('api:refresh-points')
        // 酒保积分话术执行
        events?.emit('speak', t('get-points-speak'), 'get-points')
        setTimeout(() => {
          // 关闭动画
          setIsGetPoints(false)
        }, 2000);
      }, 3000);
    })
  }, [events, t])

  useEffect(() => {
    let ins = null
    let timer = null
    // 酒保讲话
    const stepRead = (word: string, type: string) => {
      setIsSpeak(true)
      // clear 清除状态
      if (type === 'clear') {
        setIsSpeak(false)
        ins.stop = true
        clearTimeout(timer)
        timer = null
        setWord('')
        return
      }
      if (ins) {
        ins.stop = true
        clearTimeout(timer)
        timer = null
      }
      ins = typedWord(word, (letter, end, duration) => {
        if (end) {
          timer = setTimeout(() => {
            setWord('')
          }, 1500);
          setIsSpeak(false)
          events.emit('action:speak-end', type, duration)
        } else {
          setWord(letter)
        }
      }, { speed: 20 })
    }
    events.on('speak', stepRead)
  }, [events])

  // 酒保自定义话术
  const bartenderSpeak = (type: string) => {
    switch(type) {
      case 'points': {
        events?.emit('speak', t('coin-speak'), 'points')
        break
      }
      case 'secret': {
        events?.emit('speak', t('secret-speak'), 'secret')
        break
      }
      case 'bartender': {
        // 第四个语句触发几率 25% * 5% = 1.25%
        // 前三个语句触发几率 (100% - 25% * 5%) / 3 = 32.9%
        events?.emit('speak', bartenderWords[Math.floor(Math.random() * 3.05)], 'bartender')
        break
      }
      default:
        events?.emit('speak', '. . . . . .', 'unknown')
        break
    }

  }
  return (
    <div className={`private w-full h-full tarven-bar ${className}`}>
      <div className="absolute pointer-events-auto bar-bartender z-[1]">
        { isGetPoints ? (
          <Image className="pointer-events-none" alt="bartender-unbrew" src={BartenderGetPointsAnimate} layout="fill"/>
        ) : isSpeak ? (
          <Image className="pointer-events-none" alt="bartender-talk" src={BartenderTalkAnimate} layout="fill" />
        ) : (
          <Image alt="bar-bartender" src={PointsBartender} layout="fill" onClick={bartenderSpeak.bind(this, 'bartender')}/>
        ) }
      </div>
      {
        word && (
          <div className="absolute px-[16px] py-[8px] select-none text-[14px] leading-[22px] z-[8] bar-bartender-bubble">
            <span ref={bubbleRef} className="relative inline-block text-black font-medium z-[1]">
              { word }
            </span>
            <div className="absolute top-0 left-0 w-full h-full bg-white opacity-90 backdrop-blur-[6px] rounded-[8px] z-0">
              <svg className="absolute bar-bartender-bubble-icon" stroke="#fff" fill="#fff" viewBox="0 0 300 250">
                <polygon strokeLinejoin="round" points="0 50, 50 50, 250 200, 250 50, 300 50, 300 0, 0 0"/>
              </svg>
            </div>
          </div>
        )
      }
      {/* <Link passHref href="/">
        <a target="_blank">
          <div className="absolute bar-coin z-[2]" onClick={bartenderSpeak.bind(this, 'points')}></div>
        </a>
      </Link> */}
      <div className="absolute bar-coin z-[2]" onClick={bartenderSpeak.bind(this, 'points')}></div>
      <div className="absolute pointer-events-auto bar-points z-[2]" onClick={bartenderSpeak.bind(this, 'points')}></div>
      <div className="absolute pointer-events-none bar-cabinet z-[3]">
        <Link href={{ pathname: '/mall' }} target="_blank">

          <div className="absolute pointer-events-auto bar-exhibit" data-idx="0"></div>

        </Link>
        <Link href={{ pathname: '/mall' }} target="_blank">

          <div className="absolute pointer-events-auto bar-exhibit" data-idx="1"></div>

        </Link>
        <Link href={{ pathname: '/mall' }} target="_blank">

          <div className="absolute pointer-events-auto bar-exhibit" data-idx="2"></div>

        </Link>
        <Link href={{ pathname: '/mall' }} target="_blank">

          <div className="absolute pointer-events-auto bar-exhibit-small"></div>

        </Link>
        <div className="absolute pointer-events-auto bar-exhibit-secret" onClick={bartenderSpeak.bind(this, 'secret')}>
          <HoverTip text="....?" driection="left" offset={6} />
        </div>
      </div>
    </div>
  );
}

const TarvenBar = styled(_TarvenBar)`
  &.tarven-bar {
    .bar {
      &-bartender {
        left: 59px;
        bottom: 117px;
        width: 206px;
        height: 191px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          left: 80px;
          bottom: 158px;
          width: 275px;
          height: 255px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          left: 106px;
          bottom: 208px;
          width: 366px;
          height: 340px;
        }
        &-bubble {
          right: 180px;
          bottom: 240px;
          width: 202px;
          @media (max-width: 440px) {
            padding: 24px 48px;
            font-size: 36px;
            line-height: 66px;
            right: 40px;
            bottom: 460px;
            width: 606px;
            transform: translateX(-50%);
          }
          @media (min-width: 1920px) {
            right: 240px;
            bottom: 320px;
            width: 202px;
          }
          @media (min-width: 2560px)  {
            right: 320px;
            bottom: 426px;
            width: 202px;
          }
          &-icon {
            width: 24px;
            height: 20px;
            stroke-width: 40px;
            bottom: -13px;
            right: 20px;
            @media (max-width: 440px) {
              width: 72px;
              height: 60px;
              stroke-width: 60px;
              bottom: -42px;
              right: 20px;
            }
          }
        }
      }
      &-coin {
        right: 18px;
        bottom: 0;
        width: 46px;
        height: 46px;
        // background-color: rgba(0, 0, 0, .6);
        &:hover {
          &::before {
            visibility: visible;
          }
        }
        &::before {
          content: '';
          position: absolute;
          top: 10%;
          left: 10%;
          width: 80%;
          height: 80%;
          background: rgb(255, 198, 53, .6);
          filter: blur(14px);
          visibility: hidden;
          z-index: 1;
          pointer-events: none;
        }
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          right: 26px;
          width: 60px;
          height: 60px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          right: 34px;
          width: 80px;
          height: 80px;
        }
      }
      &-points {
        right: 16px;
        bottom: 120px;
        width: 90px;
        height: 75px;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          right: 22px;
          bottom: 168px;
          width: 120px;
          height: 100px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          right: 29px;
          bottom: 224px;
          width: 160px;
          height: 134px;
        }
      }
      &-cabinet {
        top: 0;
        left: 46px;
        width: 215px;
        height: 155px;
        // border: 2px solid blue;
        // box-sizing: border-box;
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          left: 62px;
          width: 280px;
          height: 200px;
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          left: 84px;
          width: 373px;
          height: 266px;
        }
      }
      &-exhibit {
        left: var(--x);
        top: 0px;
        width: 50px;
        height: 54px;
        // background-color: rgba(0, 0, 0, .4);
        &[data-idx="0"] {
          --x: 12px;
        }
        &[data-idx="1"] {
          --x: 68px;
        }
        &[data-idx="2"] {
          --x: 151px;
        }
        @media (max-width: 440px) {}
        @media (min-width: 1920px) {
          width: 69px;
          height: 63px;
          &[data-idx="0"] {
            --x: 16px;
          }
          &[data-idx="1"] {
            --x: 88px;
          }
          &[data-idx="2"] {
            --x: 196px;
          }
        }
        @media (min-width: 2560px), (max-width: 440px)  {
          width: 92px;
          height: 84px;
          &[data-idx="0"] {
            --x: 21px;
          }
          &[data-idx="1"] {
            --x: 117px;
          }
          &[data-idx="2"] {
            --x: 261px;
          }
        }
        &-small {
          right: 2px;
          top: 80px;
          width: 47px;
          height: 42px;
          @media (max-width: 440px) {}
          @media (min-width: 1920px) {
            right: 2px;
            top: 100px;
            width: 61px;
            height: 54px;
          }
          @media (min-width: 2560px), (max-width: 440px)  {
            right: 3px;
            top: 135px;
            width: 81px;
            height: 72px;
          }
        }
        &-secret {
          left: 0px;
          top: 59px;
          width: 62px;
          height: 62px;
          @media (max-width: 440px) {}
          @media (min-width: 1920px) {
            top: 80px;
            width: 80px;
            height: 80px;
          }
          @media (min-width: 2560px), (max-width: 440px)  {
            top: 108px;
            width: 106px;
            height: 106px;
          }
        }
      }
    }
  }
`

